import { useEffect, useReducer } from 'react';

export const preloadImage = (src: string): Promise<string> =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.src = src;
    img.onload = () => {
      resolve(src);
    };
    img.onerror = () => {
      reject(`Image not found URL: ${src}`);
    };
  });

export const preloadImages = async (images: string[], keys: string[]) => {
  const result = await Promise.all(images.map((image) => preloadImage(image)));

  return keys.reduce(
    (acc, key, index) => ({ ...acc, [key]: result[index] }),
    {}
  );
};

type ImageState = {
  loadedImage: string | undefined;
  loading: boolean;
  error: boolean;
};

const initialState: ImageState = {
  loadedImage: undefined,
  loading: false,
  error: false,
};

enum ACTION_TYPES {
  LOADING = 0,
  LOADED = 1,
  ERROR = 2,
}

type ImageAction =
  | { type: ACTION_TYPES.LOADING }
  | {
      type: ACTION_TYPES.LOADED;
      payload: { loadedImage: string };
    }
  | { type: ACTION_TYPES.ERROR };

function reducer(state: ImageState, action: ImageAction): ImageState {
  switch (action.type) {
    case ACTION_TYPES.LOADING:
      return {
        ...state,
        loadedImage: undefined,
        loading: true,
        error: false,
      };
    case ACTION_TYPES.LOADED:
      return {
        ...state,
        loadedImage: action.payload.loadedImage,
        loading: false,
        error: false,
      };
    case ACTION_TYPES.ERROR:
      return {
        ...state,
        loadedImage: undefined,
        loading: false,
        error: true,
      };
    default:
      return state;
  }
}

export function useLoadImage(src?: string) {
  const [state, dispatch] = useReducer(reducer, initialState);

  useEffect(() => {
    const action = async () => {
      dispatch({ type: ACTION_TYPES.LOADING });

      if (src == null) {
        dispatch({ type: ACTION_TYPES.ERROR });
        return;
      }

      try {
        const loadedImage = await preloadImage(src);
        dispatch({ type: ACTION_TYPES.LOADED, payload: { loadedImage } });
      } catch {
        dispatch({ type: ACTION_TYPES.ERROR });
      }
    };

    action();
  }, [src]);

  return state;
}
